import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { toSentenceCase } from '../lib/format'
import { Shell } from '../components/Shell'
import { Button, Field, MiniSpin, Sill, Check } from '../components/ui'
import AvatarUpload from '../components/AvatarUpload'

// Matches Me.jsx's STATE_NAMES exactly — two screens write the same column
// and must agree on the value set, not just the constraint the DB enforces.
const STATE_NAMES = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', WA: 'Western Australia',
  SA: 'South Australia', TAS: 'Tasmania', ACT: 'Australian Capital Territory', NT: 'Northern Territory',
}

/**
 * Everything signup deliberately doesn't ask.
 *
 * Getting an account now costs one tap on a provider, or an email and a code.
 * Name and lane are asked here instead — on the far side of the door, where
 * abandoning costs the person something and so almost nobody does. Asking first
 * put four screens in front of a stranger who had no reason yet to fill them in.
 *
 * A provider usually supplies the name, so a Google signup skips straight past
 * the name step to the lane and is in from there.
 */

/**
 * Three, not four.
 *
 * "I've been asked to co-sign" used to be a fourth tile, and it was never a
 * kind of person: handle_new_user() claims any co-writer row matching the new
 * address, so the song is already waiting on the other side. It was asking
 * someone to tell us something we already knew.
 *
 * `id` lands in the auth user's metadata and only ever picks a landing screen.
 * It is client-writable, so it must never be read as a permission —
 * church_members is still the only thing that grants anything.
 */
const LANES = [
  {
    id: 'songwriter', title: 'I write songs',
    blurb: 'Bring a song in, sort the splits, and choose who hears it.',
    icon: <path d="M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm12-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />,
  },
  {
    id: 'church', title: "I'm a pastor, or setting up a church",
    blurb: 'Add your team, shape how songs get reviewed, receive what your writers send.',
    icon: <path d="M3 21h18M5 21V10l7-6 7 6v11M10 21v-5h4v5" />,
  },
  {
    id: 'industry', title: "I'm a music professional",
    blurb: 'Label, management, publisher or A&R. This one is a request, not a signup.',
    icon: <path d="M12 3v10.6A4 4 0 1 0 14 17V7h4V3z" />,
  },
]
export default function Welcome() {
  const { user, profile, refresh } = useSession()
  const nav = useNavigate()

  const lane = user?.user_metadata?.access_type
  const [step, setStep] = useState(null)
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [bio, setBio] = useState('')
  const [tagline, setTagline] = useState('')
  const [homeState, setHomeState] = useState('')
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [company, setCompany] = useState('')
  const [position, setPosition] = useState('')
  const [website, setWebsite] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  // Which lane was tapped. `busy` alone disables all three at once, which dims
  // the answer along with the options and leaves the person mid-navigation with
  // no confirmation their tap landed on the one they meant.
  const [picked, setPicked] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!profile) return
    setFirst(profile.first_name || '')
    setLast(profile.last_name || '')
    setBio(profile.bio || '')
    setTagline(profile.tagline || '')
    setHomeState(profile.state || '')
  }, [profile])

  // Same read AvatarUpload's caller does on Me.jsx — the object key alone
  // isn't a URL, and the picker needs something to show once a photo exists
  // (a returning user who skipped this once and comes back through here).
  useEffect(() => {
    let alive = true
    if (!user || !profile?.avatar_object_key) { setAvatarUrl(null); return }
    ;(async () => {
      const { data } = await supabase.functions.invoke('r2-avatar-url', {
        body: { mode: 'read', user_id: user.id },
      })
      if (alive) setAvatarUrl(data?.url ?? null)
    })()
    return () => { alive = false }
  }, [user, profile?.avatar_object_key])

  /**
   * Name, then lane, then whatever that lane needs. A pending industry request
   * wins over the form: hitting home shouldn't invite someone to ask a second
   * time, and the table's partial unique index would refuse it anyway.
   *
   * `lane_set` is the marker for "they have answered", not access_type itself —
   * requestAccess() clears access_type when it's done, and keying off that
   * value would send every finished industry user round the loop again.
   */
  useEffect(() => {
    let alive = true
    if (!user || step) return
    ;(async () => {
      if (!profile?.first_name) { if (alive) setStep('name'); return }
      // profile_set marks "asked", same shape as lane_set below — a skip still
      // counts as asked, so this doesn't re-offer the step on every visit.
      if (!user.user_metadata?.profile_set) { if (alive) setStep('profile'); return }
      if (!user.user_metadata?.lane_set) { if (alive) setStep('lane'); return }
      if (lane !== 'industry') { nav('/home', { replace: true }); return }
      const { data } = await supabase
        .from('industry_access_requests').select('id').limit(1)
      if (alive) setStep(data?.length ? 'requested' : 'industry')
    })()
    return () => { alive = false }
  }, [user, profile, lane, step, nav])

  const run = async (fn) => {
    setBusy(true); setErr(null)
    try { await fn() } catch (e) { setErr(readableError(e)) } finally { setBusy(false) }
  }

  const saveName = () => run(async () => {
    const { error } = await supabase.from('users').upsert({
      id: user.id,
      email: user.email,
      role: 'songwriter',
      first_name: toSentenceCase(first),
      last_name: toSentenceCase(last),
      bio: bio.trim() || null,
    })
    if (error) throw error
    await refresh()
    setStep('profile')
  })

  // Skip and save both end the step the same way — mark it asked and move on.
  // A skip writes nothing; it isn't "no" to a tagline, just "not now".
  const advanceFromProfile = async () => {
    await refresh()
    setStep(user.user_metadata?.lane_set ? null : 'lane')
    if (user.user_metadata?.lane_set) {
      nav(lane === 'church' ? '/church/new' : '/home', { replace: true })
    }
  }

  const saveProfile = () => run(async () => {
    const { error } = await supabase.from('users').update({
      tagline: tagline.trim() || null,
      state: homeState || null,
    }).eq('id', user.id)
    if (error) throw error
    const { error: e2 } = await supabase.auth.updateUser({ data: { profile_set: true } })
    if (e2) throw e2
    await advanceFromProfile()
  })

  const skipProfile = () => run(async () => {
    const { error } = await supabase.auth.updateUser({ data: { profile_set: true } })
    if (error) throw error
    await advanceFromProfile()
  })

  const pickLane = (id) => {
    setPicked(id)
    return run(async () => {
      try {
        const { error } = await supabase.auth.updateUser({
          data: { access_type: id, lane_set: true },
        })
        if (error) throw error
        if (id === 'industry') { setStep('industry'); return }
        if (id === 'church') { nav('/church/new', { replace: true }); return }
        nav('/rights', { replace: true, state: { from: '/home' } })
      } catch (e) { setPicked(null); throw e }
    })
  }

  const requestAccess = () => run(async () => {
    // status/reviewed_* are left to the table's defaults on purpose: the insert
    // policy rejects a row that arrives pre-approved, so nobody grants
    // themselves access by posting a cleverer body.
    const { error } = await supabase.from('industry_access_requests').insert({
      user_id: user.id,
      company: company.trim(),
      position: position.trim() || null,
      website: website.trim() || null,
      note: note.trim() || null,
    })
    if (error) throw error
    // Retire the lane so home stops routing them back here every visit.
    await supabase.auth.updateUser({ data: { access_type: null } })
    setStep('requested')
  })

  return (
    <Shell>
      {step === 'name' && (<>
        <div className="eyebrow">Your details</div>
        <h2 className="q">Who are you?</h2>
        <p className="sub">This is what a pastor sees next to a song. Nothing more than this.</p>
        <Sill />

        {/* Your name is one answer given in two boxes; the bio is a second
            question. Equal gaps between all three made it read as three. */}
        <div className="fieldgroup">
          <Field label="First name">
            <input className="input" value={first} autoFocus autoComplete="given-name"
                   onChange={(e) => setFirst(e.target.value)} />
          </Field>
          <Field label="Last name">
            <input className="input" value={last} autoComplete="family-name"
                   onChange={(e) => setLast(e.target.value)} />
          </Field>
        </div>
        <div className="fieldgroup">
          <Field label="A line about you">
            <textarea className="ta" value={bio} onChange={(e) => setBio(e.target.value)}
                      placeholder="Optional. Where you write from." />
          </Field>
        </div>

        <Button busy={busy} disabled={!first.trim()} onClick={saveName}>Next</Button>
        {err && <p className="err">{err}</p>}
      </>)}

      {step === 'profile' && (<>
        <div className="eyebrow">Your profile</div>
        <h2 className="q">Show people who you are.</h2>
        <p className="sub">
          Other writers are about to see this — a photo and a line turn a name
          in the Collective into a person worth following. Nothing here is
          final; change it any time from your profile.
        </p>
        <Sill />

        <AvatarUpload profile={profile} url={avatarUrl}
                      onChange={async () => {
                        await refresh()
                        const { data } = await supabase.functions.invoke('r2-avatar-url', {
                          body: { mode: 'read', user_id: user.id },
                        })
                        setAvatarUrl(data?.url ?? null)
                      }} />

        <div className="fieldgroup">
          <Field label="A line about you">
            <input className="input" value={tagline} maxLength={80}
                   placeholder="Songs for small rooms"
                   onChange={(e) => setTagline(e.target.value)} />
          </Field>
        </div>
        <div className="fieldgroup">
          <Field label="Where you are">
            <select className="input" value={homeState}
                    onChange={(e) => setHomeState(e.target.value)}>
              <option value="">Prefer not to say</option>
              {Object.entries(STATE_NAMES).map(([code, full]) => (
                <option key={code} value={code}>{full}</option>
              ))}
            </select>
          </Field>
        </div>

        <Button busy={busy} onClick={saveProfile}>Save and continue</Button>
        <button className="btn btn-quiet" disabled={busy} onClick={skipProfile}
                style={{ marginTop: 10 }}>
          Skip for now
        </button>
        {err && <p className="err">{err}</p>}
      </>)}

      {step === 'lane' && (<>
        <div className="eyebrow">One more thing</div>
        <h2 className="q">What brings you here?</h2>
        <p className="sub">
          This only sets where you land. It isn't a permission, and it doesn't
          shut anything off — a pastor can write songs too.
        </p>
        <Sill />

        <div className="lanes">
          {LANES.map((l) => (
            <button key={l.id} className={`lane${picked === l.id ? ' picked' : ''}`}
                    disabled={busy} aria-busy={picked === l.id}
                    onClick={() => pickLane(l.id)}>
              <span className="lane-ic" aria-hidden="true">
                <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  {l.icon}
                </svg>
              </span>
              <span className="lane-t">
                {l.title}
                <small>{l.blurb}</small>
              </span>
              <span className="lane-ar" aria-hidden="true">
                {picked === l.id ? <MiniSpin /> : '›'}
              </span>
            </button>
          ))}
        </div>
        {err && <p className="err">{err}</p>}
      </>)}

      {step === 'industry' && (<>
        <div className="eyebrow">Industry access</div>
        <h2 className="q">Tell us who you work for.</h2>
        <p className="sub">
          Industry accounts are granted, not claimed — a writer's catalogue and splits sit
          behind this, so someone reads every request. You'll hear back by email.
        </p>
        <Sill />

        <Field label="Company">
          <input className="input" value={company} autoFocus
                 placeholder="Label, management or publisher"
                 onChange={(e) => setCompany(e.target.value)} />
        </Field>
        <Field label="Your position">
          <input className="input" value={position} placeholder="Optional. A&R, manager, sync…"
                 onChange={(e) => setPosition(e.target.value)} />
        </Field>
        <Field label="Website">
          <input className="input" type="url" value={website} placeholder="Optional"
                 onChange={(e) => setWebsite(e.target.value)} />
        </Field>
        <Field label="What are you after?">
          <textarea className="ta" value={note} placeholder="Optional. A line is plenty."
                    onChange={(e) => setNote(e.target.value)} />
        </Field>

        <Button busy={busy} disabled={!company.trim()} onClick={requestAccess}>
          Request access
        </Button>
        {err && <p className="err">{err}</p>}
        <p className="small" style={{ marginTop: 12 }}>
          Nothing opens up yet. Until this is granted your account behaves like any
          other — you can still write and submit songs of your own.
        </p>
      </>)}

      {step === 'requested' && (<>
        <div className="done-ic"><Check /></div>
        <h2 className="q center">Asked.</h2>
        <p className="sub center">
          Someone will read it and come back to you at {user?.email}. There's no
          queue position to watch and nothing further to do.
        </p>
        <Sill />
        <button className="btn btn-quiet" onClick={() => nav('/home')}>
          Carry on to the app
        </button>
      </>)}
    </Shell>
  )
}
